import React, { useState, useEffect, useRef } from "react";
import {  useSelector } from "react-redux";
import {
  FaInstagram,
  FaPlay,
  FaExternalLinkAlt,
} from "react-icons/fa";

const StorySection = () => {

  const { banners } = useSelector((state) => state.banner);
  const [selectedStory, setSelectedStory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [useFallbackMode, setUseFallbackMode] = useState(false);
  const scrollContainerRef = useRef(null);

  // Extract reels from the API response
  const reels = banners?.reels || [];

  // Note: Banners (including reels) are already fetched by AutoSwipeBanner component
  // No need to fetch again here to avoid duplicate API calls

  // Function to convert Instagram URL to embed URL
  const convertToEmbedUrl = (url) => {
    try {
      if (!url || typeof url !== 'string') return '';
      
      // Clean the URL
      let cleanUrl = url.split('?')[0];
      
      // Extract post/reel ID from URL
      const reelMatch = cleanUrl.match(/\/reel\/([^\/]+)/);
      const postMatch = cleanUrl.match(/\/p\/([^\/]+)/);
      
      if (reelMatch) {
        return `https://www.instagram.com/p/${reelMatch[1]}/embed/`;
      } else if (postMatch) {
        return `https://www.instagram.com/p/${postMatch[1]}/embed/`;
      }
      
      return cleanUrl;
    } catch (error) {
      console.warn('Error converting Instagram URL:', error);
      return '';
    }
  };

  // Transform reels data into stories format
  const stories = reels.map((reel, index) => {
    try {
      // Get the original Instagram URL from the 'link' field
      let originalUrl = reel.link || reel.instagramUrl || reel.url || `https://www.instagram.com/reel/sample${index + 1}/`;
      
      // Convert to embed URL
      const embedUrl = convertToEmbedUrl(originalUrl);
      
      return {
        id: reel.id || `reel-${index}`,
        title: reel.description || reel.name || `Reel ${index + 1}`,
        thumbnail: reel.DocImage?.imageUrl || `https://picsum.photos/150/200?random=${index + 1}`,
        instagramUrl: originalUrl, // Keep original for blockquote
        embedUrl: embedUrl, // Use converted embed URL
        type: "instagram",
      };
    } catch (error) {
      console.warn('Error processing reel:', error, reel);
      return {
        id: `error-reel-${index}`,
        title: reel?.description || `Reel ${index + 1}`,
        thumbnail: `https://picsum.photos/150/200?random=${index + 1}`,
        instagramUrl: `https://www.instagram.com/reel/sample${index + 1}/`,
        embedUrl: `https://www.instagram.com/p/sample${index + 1}/embed/`,
        type: "instagram",
      };
    }
  });

  // Use only real API data - no hardcoded fallback
  const finalStories = stories;

  const handleStoryClick = (story) => {
    // Don't open modal if we were dragging
    if (isDragging) return;
    setSelectedStory(story);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedStory(null);
  };

  const openExternalLink = (url) => {
    window.open(url, "_blank");
  };

  // Click and drag functionality
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    scrollContainerRef.current.style.cursor = 'grabbing';
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    scrollContainerRef.current.style.cursor = 'grab';
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    scrollContainerRef.current.style.cursor = 'grab';
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && isModalOpen) {
        closeModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener("keydown", handleEscapeKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen]);



  // Add mouse wheel scroll functionality
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleWheel = (e) => {
      // Only handle wheel events when hovering over the stories container
      if (scrollContainer.contains(e.target)) {
        // Prevent default vertical scroll
        e.preventDefault();
        
        // Scroll horizontally based on wheel delta
        const scrollAmount = e.deltaY > 0 ? 150 : -150;
        scrollContainer.scrollLeft += scrollAmount;
      }
    };

    // Add event listener to document to catch wheel events
    document.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // Add click and drag event listeners
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    // Add mouse move and up listeners to document for better drag experience
    const handleGlobalMouseMove = (e) => {
      if (isDragging) {
        handleMouseMove(e);
      }
    };

    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleMouseUp();
      }
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, startX, scrollLeft]);

  // Simplified approach - use only iframe to avoid Instagram script errors
  useEffect(() => {
    if (isModalOpen && selectedStory) {
      // Set fallback mode immediately to avoid Instagram script issues
      setUseFallbackMode(true);
      
      // Add global error handler to catch any remaining errors
      const errorHandler = (event) => {
        if (event.filename && event.filename.includes('embed.js')) {
          console.warn('Instagram script error prevented:', event.message);
          event.preventDefault();
          event.stopPropagation();
          return false;
        }
      };
      
      window.addEventListener('error', errorHandler);
      
      return () => {
        window.removeEventListener('error', errorHandler);
      };
    }
  }, [isModalOpen, selectedStory]);

  // Function to convert Instagram URL to embed URL
  const getInstagramEmbedUrl = (instagramUrl) => {
    // Extract the post ID from Instagram URL
    const match =
      instagramUrl.match(/\/p\/([^\/\?]+)/) ||
      instagramUrl.match(/\/reel\/([^\/\?]+)/);
    if (match) {
      // Use correct embed format for Instagram
      return `https://www.instagram.com/p/${match[1]}/embed/`;
    }
    return instagramUrl;
  };



  return (
    <div className="w-full px-2 md:px-4">
      <div className="max-w-6xl mx-auto">
        {/* Stories Container */}
        <div className="relative flex justify-center">
          <div
            ref={scrollContainerRef}
            className="flex space-x-2 md:space-x-4 overflow-x-auto pb-2 scrollbar-hide max-w-fit"
            style={{ 
              scrollBehavior: 'smooth',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              cursor: 'grab'
            }}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
          >
            {finalStories.map((story) => (
              <div
                key={story.id}
                onClick={() => handleStoryClick(story)}
                className="flex-shrink-0 cursor-pointer group"
              >
                <div className="relative w-16 h-20 md:w-24 md:h-32 lg:w-28 lg:h-36 rounded-xl md:rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                  {/* Story Image */}
                  <img
                    src={story.thumbnail}
                    alt={story.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback to a solid color background with text
                      e.target.style.display = "none";
                      const fallbackDiv = e.target.nextSibling;
                      if (fallbackDiv) {
                        fallbackDiv.style.display = "flex";
                      }
                    }}
                  />

                  {/* Fallback Background */}
                  <div className="hidden w-full h-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center">
                    <div className="text-center text-white">
                      <FaPlay className="text-xl md:text-2xl mx-auto mb-1 md:mb-2" />
                      <div className="bg-black/60 backdrop-blur-sm rounded-md px-1 py-0.5 md:px-2 md:py-1">
                        <p className="text-[8px] md:text-xs font-bold px-1">{story.title}</p>
                      </div>
                    </div>
                  </div>

                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white/90 rounded-full p-2">
                      <FaPlay className="text-gray-800 text-sm" />
                    </div>
                  </div>

                  {/* Story Title */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1 md:p-1.5">
                    <div className="bg-black/60 backdrop-blur-sm rounded-md px-1 py-0.5 md:px-2 md:py-1">
                      <p className="text-white font-bold truncate leading-tight" style={{ fontSize: '8px' }}>
                        <span className="md:hidden">{story.title}</span>
                        <span className="hidden md:inline" style={{ fontSize: '12px' }}>{story.title}</span>
                      </p>
                    </div>
                  </div>

                  {/* Platform Icon */}
                  <div className="absolute top-2 right-2">
                    <FaPlay className="text-white text-xs bg-black/60 rounded-full p-1" />
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* Video Modal */}
      {isModalOpen && selectedStory && (
        <div 
          className="fixed inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          style={{ zIndex: 99999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >
          <div 
            className="relative w-full max-w-2xl mx-4 bg-white rounded-2xl overflow-hidden shadow-2xl"
            style={{ zIndex: 100000 }}
          >

            {/* Modal Header */}
            <div className="flex items-center justify-between p-2 md:p-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white">
              <div className="flex items-center space-x-1 md:space-x-2">
                <FaPlay className="text-white text-xs md:text-sm" />
                <h3 className="text-xs md:text-sm font-medium truncate max-w-[150px] md:max-w-[200px]">{selectedStory.title}</h3>
              </div>
              {/* Debug Info */}
              <div className="text-xs text-white/80 hidden md:block">
                <div>
                  URL: {getInstagramEmbedUrl(selectedStory.instagramUrl)}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => openExternalLink(selectedStory.instagramUrl)}
                  className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                  title="Open in new tab"
                >
                  <FaExternalLinkAlt className="text-sm" />
                </button>
                <button
                  onClick={closeModal}
                  className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                  title="Close"
                >
                  <span className="text-white text-xl font-bold">×</span>
                </button>
              </div>
            </div>

            {/* Instagram Embed */}
            <div className="relative bg-white" style={{
              height: window.innerWidth < 768 ? "60vh" : "70vh", 
              minHeight: window.innerWidth < 768 ? "400px" : "600px", 
              maxHeight: window.innerWidth < 768 ? "500px" : "700px"
            }}> 
              <div className="flex items-center justify-center" style={{height : "100%"}}>
                {useFallbackMode ? (
                  /* Fallback mode - direct iframe only */
                  <iframe
                    src={selectedStory.embedUrl}
                    width="400"
                    height="600"
                    frameBorder="0"
                    scrolling="no"
                    allowtransparency="true"
                    style={{
                      maxWidth: '400px',
                      width: '100%',
                      height: '100%'
                    }}
                    onError={(e) => {
                      console.warn('Error loading iframe in fallback mode:', e);
                    }}
                  />
                ) : (
                  <>
                    {/* Try blockquote embed first */}
                    <blockquote 
                      className="instagram-media" 
                      data-instgrm-permalink={selectedStory.instagramUrl}
                      data-instgrm-version="14" 
                      style={{margin:0, padding:0, width: "100%", height: "100%", maxWidth: "400px"}}
                    >
                    </blockquote>
                    
                    {/* Fallback iframe if blockquote doesn't work */}
                    <iframe
                      src={selectedStory.embedUrl}
                      width="400"
                      height="600"
                      frameBorder="0"
                      scrolling="no"
                      allowtransparency="true"
                      style={{
                        display: 'none',
                        maxWidth: '400px',
                        width: '100%',
                        height: '100%'
                      }}
                      onLoad={(e) => {
                        try {
                          // Show iframe if blockquote fails to load
                          setTimeout(() => {
                            const blockquote = e.target.previousSibling;
                            if (blockquote && blockquote.children.length === 0) {
                              e.target.style.display = 'block';
                              blockquote.style.display = 'none';
                            }
                          }, 2000);
                        } catch (error) {
                          console.warn('Error in iframe onLoad:', error);
                        }
                      }}
                      onError={(e) => {
                        console.warn('Error loading iframe:', e);
                        setUseFallbackMode(true); // Switch to fallback mode
                      }}
                    />
                  </>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FaInstagram className="text-pink-500" />
                  <span className="text-sm text-gray-600">
                    Watch on Instagram
                  </span>
                </div>
                <button
                  onClick={() => openExternalLink(selectedStory.instagramUrl)}
                  className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full text-sm font-medium hover:from-pink-600 hover:to-purple-700 transition-all duration-300"
                >
                  View on Instagram
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StorySection;
